import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { truncateByCharacters } from '../../../../../../../../utils/utils';
import styles from '../DealsRow/DealsRow.module.scss';
import Tooltip from '../../../../../../../../common/_custom/Tooltip/Tooltip';
import CaretIcon from '../../../../../../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../../../../../../utils/constants/colors';
import MultiSelectWithDisabledOptions from '../../../../../../../../common/_custom/MultiSelectDropdown/MultiSelectWithDisabledOptions';
import { DealStatus } from '../../../../../../../../store/investor/reducer';
import { USER_TYPES } from '../../../../../../../../store/investor/constants';
import { DESIGNATION } from '../../../../../../../../utils/constants/commonConstants';
import { useOutsideClick } from '../../../../../../../../utils/constants/hooks';

interface User {
    name: string;
    userId: string;
    ownerType: 'LENDER' | 'LENDER_TEAM';
}

export interface Option {
    label: string;
    value: string;
    type?: string;
}

interface Props {
    users: User[];
    dealId: string;
    updateLenderUsers: (selected: string[], options: Option[], dealId: string) => void;
    fetchLenderUsers: (dealReceiverId: string) => Promise<Option[]>;
    dealReceiverId: string;
    dealStatus?: string;
}

const LenderUserCell: React.FC<Props> = ({ users, dealId, updateLenderUsers, fetchLenderUsers, dealReceiverId, dealStatus }) => {
    /** Tracks selected user IDs */
    const [selectedLenderUsers, setSelectedLenderUsers] = useState<string[]>([]);
    const [allUserOptions, setAllUserOptions] = useState<Option[]>([]);

    useEffect(() => {
        if (users) {
            const userOptions = users.map((user) => ({
                label: user.name,
                value: user.userId,
                type: user.ownerType === USER_TYPES.LENDER ? DESIGNATION.ADMIN : DESIGNATION.TEAM_MEMBER,
            }));
            setAllUserOptions(userOptions);
            setSelectedLenderUsers(userOptions.map((option) => option.value));
        }
    }, [users]);

    /** All available user options for selection (fetched from API on open) */

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    /** Ref to persist selected users across renders (used during dropdown close) */
    const selectedLenderUsersRef = useRef<string[]>(selectedLenderUsers);

    /** List of userIds that should be disabled (LENDER type) */
    const disabledUserIds = useMemo(() => users.filter((user) => user.ownerType === 'LENDER').map((user) => user.userId), [users]);

    /** Ref to track the internal dropdown open state for outside click */
    const isDropdownOpenRef = useRef<boolean>(isDropdownOpen);

    const allUserOptionsRef = useRef<Option[]>(allUserOptions);
    const containerRef = useRef<HTMLDivElement | null>(null);

    /** Close dropdown when clicking outside
     * This effect listens for mousedown events and checks if the click is outside the dropdown container.
     * If user clicks outside LenderUsersContainer, this effect resets the dropdown state.
     * This ensures that the dropdown closes when the user interacts with other parts of the UI.
     */
    useOutsideClick(containerRef, () => {
        if (isDropdownOpenRef.current) {
            setIsDropdownOpen(false);
        }
    });

    useEffect(() => {
        if (isDropdownOpen && dealReceiverId) {
            fetchLenderUsers(dealReceiverId)
                .then((fetchedOptions) => {
                    setAllUserOptions(fetchedOptions);
                })
                .catch((error) => {
                    console.error('Failed to fetch lender users:', error);
                });
        }
    }, [isDropdownOpen, dealReceiverId, fetchLenderUsers]);

    useEffect(() => {
        selectedLenderUsersRef.current = selectedLenderUsers;
    }, [selectedLenderUsers]);

    useEffect(() => {
        isDropdownOpenRef.current = isDropdownOpen;
    }, [isDropdownOpen]);

    useEffect(() => {
        allUserOptionsRef.current = allUserOptions;
    }, [allUserOptions]);

    const handleToggleSelection = (newSelected: string[]) => {
        setSelectedLenderUsers(newSelected);
    };

    const selectedUserObjects = allUserOptions.filter((u) => selectedLenderUsers.includes(u.value));
    const firstName = truncateByCharacters(selectedUserObjects[0]?.label || '', 20);
    const remainingUsers = selectedUserObjects.slice(1);

    return (
        <div ref={containerRef}>
            <div
                className={styles.LenderUsersContainer}
                onClick={(e) => {
                    e.stopPropagation();
                    if (dealStatus === DealStatus.DRAFT) return;
                    setIsDropdownOpen((prev) => !prev);
                }}
            >
                <span className={styles.LenderUsersText} style={{ color: '#000000' }}>
                    {firstName}
                </span>
                {remainingUsers.length > 0 && (
                    <Tooltip
                        direction="bottom"
                        style={{ width: 'fit-content', left: '-30px' }}
                        content={
                            <ul style={{ margin: 0, paddingLeft: '1em' }}>
                                {remainingUsers.map((user, index) => (
                                    <li key={index}>{user.label}</li>
                                ))}
                            </ul>
                        }
                    >
                        <span className={styles.More}> +{remainingUsers.length} more</span>
                    </Tooltip>
                )}
                {dealStatus !== DealStatus.DRAFT && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <CaretIcon colour={COLORS.BLACK} height="14" width="14" />
                    </div>
                )}
            </div>

            {isDropdownOpen && dealStatus !== DealStatus.DRAFT && (
                <div style={{ position: 'relative' }}>
                    <MultiSelectWithDisabledOptions
                        options={allUserOptions}
                        disabledOptions={disabledUserIds}
                        selectedItems={selectedLenderUsers}
                        placeholder=""
                        toggleSelection={handleToggleSelection}
                        closeDropdown={(options) => {
                            try {
                                updateLenderUsers(selectedLenderUsersRef.current, options, dealId);
                            } catch (err) {
                                console.error('Error updating lender users:', err);
                                setSelectedLenderUsers([]);
                            }
                        }}
                        showInput={false}
                        dropdownStyle={{ width: '300px', left: '0px' }}
                    />
                </div>
            )}
        </div>
    );
};

export default LenderUserCell;
